import { useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  RegistrationDialog,
  isAuthenticated,
  type LoginFormData,
  type RegistrationFormData,
} from '@alchemiesolns/product-registration';
import { loginWithFirebaseForProduct } from '../loginWithFirebase';
import { registerFirebaseAccount } from '../registerFirebaseAccount';
import { PRODUCT_ID } from '../constants/product';
import { appendIntentToPath, buildAuthRedirectSearch, postAuthDestination } from '../auth/navigation';
import { sendPasswordResetEmail, sendVerificationEmail, useAuthUser } from '@alchemiesolns/user-access';
import piphWordmarkUrl from '../assets/piph-wordmark.png';
import './AuthPage.css';

type AuthPageMode = 'login' | 'register';

function verificationEmailFollowupBlurb(emailFromQuery: string | null): string {
  const trimmed = emailFromQuery?.trim();
  const tail =
    'Open the link in that message to confirm your address, then sign in below. If you do not see it within a few minutes, check your spam folder.';
  if (trimmed) {
    return `We sent a verification email to ${trimmed}. ${tail}`;
  }
  return `We sent a verification email. ${tail}`;
}

export function AuthPage({ mode }: { mode: AuthPageMode }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, ready } = useAuthUser();
  const bypassBilling = import.meta.env.VITE_BYPASS_BILLING === 'true';
  /** True while registerFirebaseAccount runs — signup() signs in briefly before signOut(); without this, `user` triggers a redirect to /app and the UI flashes. */
  const [registrationPending, setRegistrationPending] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotCooldownSeconds, setForgotCooldownSeconds] = useState(0);
  const [forgotInfo, setForgotInfo] = useState('');
  const [forgotError, setForgotError] = useState('');
  /** Forces RegistrationDialog remount so verification copy is visible after signup. */
  const [postRegisterUiGeneration, setPostRegisterUiGeneration] = useState(0);
  const [postRegisterSuccessCopy, setPostRegisterSuccessCopy] = useState<string | null>(null);

  useEffect(() => {
    if (user?.emailVerified) {
      setPostRegisterSuccessCopy(null);
    }
  }, [user?.emailVerified]);

  const loginVerificationBanner = useMemo(() => {
    if (mode !== 'login') return undefined;
    return (
      postRegisterSuccessCopy ??
      (searchParams.get('registered') === '1'
        ? verificationEmailFollowupBlurb(searchParams.get('email'))
        : undefined)
    );
  }, [mode, postRegisterSuccessCopy, searchParams]);

  if (bypassBilling) {
    return <Navigate to={postAuthDestination(searchParams)} replace />;
  }

  // Keep in sync with `resolveRootRedirect`: once `ready`, licensed + no Firebase user must show this form, not a spinner.
  if (!ready) {
    return <div className="auth-page-loading">Loading…</div>;
  }

  if (user && user.emailVerified && !registrationPending) {
    if (isAuthenticated()) {
      return <Navigate to={postAuthDestination(searchParams)} replace />;
    }
    return <Navigate to={appendIntentToPath('/app', searchParams)} replace />;
  }

  const goAfterAuth = () => {
    navigate(postAuthDestination(searchParams), { replace: true });
  };

  const handleSubmit = async (formData: RegistrationFormData) => {
    flushSync(() => {
      setRegistrationPending(true);
    });
    const emailTrimmed = formData.email.trim();
    try {
      await registerFirebaseAccount(formData);
      const inlineCopy = verificationEmailFollowupBlurb(emailTrimmed);
      flushSync(() => {
        setPostRegisterSuccessCopy(inlineCopy);
        setPostRegisterUiGeneration((n) => n + 1);
      });
      const raw = buildAuthRedirectSearch(searchParams);
      const loginParams = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
      loginParams.set('email', emailTrimmed);
      loginParams.set('registered', '1');
      const qs = loginParams.toString();
      navigate(`/login${qs ? `?${qs}` : ''}`, { replace: true });
    } catch (e) {
      const message = e instanceof Error ? e.message : '';
      if (message.toLowerCase().includes('already exists')) {
        throw new Error(
          'An account with this email already exists. No new account was created. Use Log in instead.'
        );
      }
      throw e;
    } finally {
      setRegistrationPending(false);
    }
  };

  const handleLogin = async (formData: LoginFormData) => {
    const result = await loginWithFirebaseForProduct(PRODUCT_ID, formData);
    if (result.kind === 'licensed') {
      goAfterAuth();
    } else {
      navigate(appendIntentToPath('/app', searchParams), { replace: true });
    }
  };

  const handleClose = () => {
    if (isAuthenticated() || (user && user.emailVerified)) {
      goAfterAuth();
    }
  };

  const switchSearch = buildAuthRedirectSearch(searchParams);

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (forgotBusy || forgotCooldownSeconds > 0) return;
    const email = forgotEmail.trim();
    if (!email) {
      setForgotError('Email is required.');
      setForgotInfo('');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setForgotError('Invalid email address.');
      setForgotInfo('');
      return;
    }
    setForgotBusy(true);
    setForgotError('');
    setForgotInfo('');
    try {
      await sendPasswordResetEmail(email);
      setForgotInfo('If an account exists for this email, a reset link has been sent.');
      setForgotCooldownSeconds(30);
      const timer = window.setInterval(() => {
        setForgotCooldownSeconds((prev) => {
          if (prev <= 1) {
            window.clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (e) {
      if (e instanceof Error && e.message.includes('Too many')) {
        setForgotError('Too many reset attempts. Try again later.');
      } else {
        setForgotError('Unable to send reset email right now.');
      }
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="auth-page-shell">
      <div className="auth-page-frame">
        <RegistrationDialog
          key={mode === 'login' ? `login-${postRegisterUiGeneration}` : 'register'}
          isOpen
          onClose={handleClose}
          onLoginSubmit={handleLogin}
          onForgotPasswordClick={() => {
            setForgotEmail(searchParams.get('email') ?? '');
            setForgotError('');
            setForgotInfo('');
            setForgotOpen(true);
          }}
          onSubmit={handleSubmit}
          onResendVerificationEmail={() => sendVerificationEmail()}
          initialMode={mode}
          initialLoginEmail={mode === 'login' ? searchParams.get('email') ?? undefined : undefined}
          initialPostRegisterSuccessMessage={loginVerificationBanner}
          onSwitchToRegister={() => navigate(`/signup${switchSearch}`, { replace: true })}
          onSwitchToLogin={() => navigate(`/login${switchSearch}`, { replace: true })}
          title={mode === 'login' ? 'Welcome back' : 'Create an account'}
          subtitle={mode === 'login' ? 'Sign in to access Piph.' : 'Get started with Piph.'}
          registerBrandKicker="by Alchemie"
          registerTitleLogoSrc={piphWordmarkUrl}
          requireRegistrationConsent
          registerShowCancel={false}
          registrationConsentText={(
            <>
              I agree to the{' '}
              <a href="https://www.alchem.ie/eula-piph-author" target="_blank" rel="noreferrer">
                terms and conditions
              </a>
            </>
          )}
        />
      </div>
      {forgotOpen && (
        <div className="auth-page-forgot-modal-overlay" onClick={() => !forgotBusy && setForgotOpen(false)}>
          <div
            className="auth-page-forgot-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-password-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="forgot-password-title" className="auth-page-forgot-title">Reset your password</h2>
            <p className="auth-page-forgot-subtitle">Enter your account email and we will send a reset link.</p>
            <form className="auth-page-forgot-form" onSubmit={handleForgotSubmit}>
              <label htmlFor="forgot-email" className="auth-page-forgot-label">Email address</label>
              <input
                id="forgot-email"
                type="email"
                value={forgotEmail}
                onChange={(e) => {
                  setForgotEmail(e.target.value);
                  setForgotError('');
                }}
                className={`auth-page-forgot-input${forgotError ? ' auth-page-forgot-input--error' : ''}`}
                autoComplete="email"
                disabled={forgotBusy}
                required
              />
              {forgotError && <p className="auth-page-forgot-error">{forgotError}</p>}
              {forgotInfo && <p className="auth-page-forgot-success">{forgotInfo}</p>}
              <div className="auth-page-forgot-actions">
                <button type="button" className="auth-page-forgot-cancel" onClick={() => setForgotOpen(false)} disabled={forgotBusy}>
                  Cancel
                </button>
                <button type="submit" className="auth-page-forgot-button" disabled={forgotBusy || forgotCooldownSeconds > 0}>
                  {forgotCooldownSeconds > 0 ? `Send reset link (${forgotCooldownSeconds}s)` : 'Send reset link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
